import { useEffect, useState } from "react";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import {
  Home, Plus, Pencil, Trash2, MapPin, Star, Loader2, CheckCircle2, Layers,
} from "lucide-react";
import MultiPropertyBookingDialog from "./MultiPropertyBookingDialog";
import { PropertyAutopayToggle, RecurringExtrasList } from "./PropertyBillingControls";

/**
 * PropertiesSection — iter255 (2026-08-24)
 *
 * Multi-property support · Phase 1.
 *
 * Owner directive: a customer account can carry many properties (Home,
 * Rental, Property #2, …).  Each property stores its OWN address,
 * square footage, yard-area default, and optional service defaults so
 * pricing per property never mixes with any other property on the
 * same account.
 *
 * Bookings + payment methods stay under the single customer account;
 * only address / lawn size / yard area travel with a property.
 */

const YARD_OPTIONS = [
  { key: "front", label: "Front Yard" },
  { key: "back",  label: "Back Yard" },
  { key: "both",  label: "Full Yard" },
];

const EMPTY_FORM = {
  label: "", address: "", square_feet: "", yard_area: "both", notes: "",
};

export default function PropertiesSection({ onChanged }) {
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [editing, setEditing] = useState(null);      // property_id | "new" | null
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(null);            // "save" | "delete" | property_id
  const [confirmDelete, setConfirmDelete] = useState(null); // property doc
  const [multiOpen, setMultiOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [propsResp, schedResp] = await Promise.all([
        api.get("/me/properties"),
        api.get("/me/recurring/list").catch(() => ({ data: { schedules: [] } })),
      ]);
      const list = propsResp?.data?.properties || [];
      const schedules = schedResp?.data?.schedules || [];
      const byPid = {};
      for (const s of schedules) byPid[s.property_id] = s;
      const enriched = list.map((p) => ({
        ...p,
        recurring: byPid[p.property_id] || null,
      }));
      setProperties(enriched);
      if (typeof onChanged === "function") onChanged(enriched);
    } catch (e) {
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-line */ }, []);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditing("new");
  };

  const openEdit = (p) => {
    setForm({
      label: p.label || "",
      address: p.address || "",
      square_feet: p.square_feet ?? "",
      yard_area: p.yard_area || "both",
      notes: p.notes || "",
    });
    setEditing(p.property_id);
  };

  const closeDialog = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const save = async () => {
    if (!form.address || !form.address.trim()) {
      toast.error("Enter the property address");
      return;
    }
    const sqft = Number.parseInt(form.square_feet, 10);
    if (!Number.isFinite(sqft) || sqft <= 0) {
      toast.error("Enter a valid lawn size in square feet");
      return;
    }
    setBusy("save");
    const body = {
      label: (form.label || "").trim() || undefined,
      address: form.address.trim(),
      square_feet: sqft,
      yard_area: form.yard_area,
      notes: (form.notes || "").trim() || null,
    };
    try {
      if (editing === "new") {
        await api.post("/me/properties", body);
        toast.success("Property added.");
      } else {
        await api.patch(`/me/properties/${editing}`, body);
        toast.success("Property updated.");
      }
      await refresh();
      closeDialog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save property");
    } finally {
      setBusy(null);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setBusy("delete");
    try {
      await api.delete(`/me/properties/${confirmDelete.property_id}`);
      toast.success("Property removed.");
      await refresh();
      setConfirmDelete(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove this property");
    } finally {
      setBusy(null);
    }
  };

  const setDefault = async (p) => {
    if (p.is_default) return;
    setBusy(p.property_id);
    try {
      await api.post(`/me/properties/${p.property_id}/set-default`);
      toast.success(`${p.label || "Property"} set as default.`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update default");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-6" data-testid="my-properties-section">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#047857] font-extrabold flex items-center gap-1.5">
            <Home className="w-3 h-3" /> My Properties
          </div>
          <h3 className="mt-0.5 text-xl sm:text-2xl font-extrabold tracking-tight font-[Outfit] text-gray-900">
            All my service addresses
          </h3>
        </div>
        <div className="flex gap-2 flex-wrap">
          {properties.length >= 2 && (
            <Button
              onClick={() => setMultiOpen(true)}
              variant="outline"
              className="rounded-full h-10 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              data-testid="book-multiple-properties-btn"
            >
              <Layers className="w-4 h-4 mr-1.5" /> Book multiple properties
            </Button>
          )}
          <Button
            onClick={openNew}
            className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-10"
            data-testid="add-property-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add Property
          </Button>
        </div>
      </div>
      <MultiPropertyBookingDialog open={multiOpen} onOpenChange={setMultiOpen} properties={properties} />

      {loading ? (
        <div className="mt-4 py-6"><Loader2 className="w-5 h-5 animate-spin text-[#047857]" /></div>
      ) : properties.length === 0 ? (
        <div className="mt-4 bg-white rounded-2xl border border-gray-200 p-8 text-center" data-testid="properties-empty">
          <MapPin className="w-9 h-9 text-gray-400 mx-auto" />
          <p className="mt-3 text-gray-600 text-sm">
            You don&apos;t have any saved properties yet. Add your first address
            to start booking service.
          </p>
        </div>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {properties.map((p) => (
            <PropertyCard
              key={p.property_id}
              p={p}
              onEdit={() => openEdit(p)}
              onDelete={() => setConfirmDelete(p)}
              onSetDefault={() => setDefault(p)}
              busyId={busy}
              onRefresh={refresh}
            />
          ))}
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="sm:max-w-md" data-testid="property-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing === "new" ? "Add a property" : "Edit property"}
            </DialogTitle>
            <DialogDescription>
              Each property keeps its own lawn size and pricing.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label htmlFor="prop-label">Name (optional)</Label>
              <Input
                id="prop-label"
                placeholder="Home, Rental, Property #2…"
                value={form.label}
                onChange={(e) => setForm((s) => ({ ...s, label: e.target.value }))}
                data-testid="property-label-input"
              />
            </div>
            <div>
              <Label htmlFor="prop-address">Address</Label>
              <Input
                id="prop-address"
                placeholder="123 Main St Jacksonville FL"
                value={form.address}
                onChange={(e) => setForm((s) => ({ ...s, address: e.target.value }))}
                data-testid="property-address-input"
              />
            </div>
            <div>
              <Label htmlFor="prop-sqft">Lawn size (sq ft)</Label>
              <Input
                id="prop-sqft"
                type="number" min="0" inputMode="numeric"
                placeholder="8500"
                value={form.square_feet}
                onChange={(e) => setForm((s) => ({ ...s, square_feet: e.target.value }))}
                data-testid="property-sqft-input"
              />
            </div>
            <div>
              <Label>Yard area</Label>
              <div className="mt-1 grid grid-cols-3 gap-2">
                {YARD_OPTIONS.map((o) => (
                  <button key={o.key} type="button"
                          onClick={() => setForm((s) => ({ ...s, yard_area: o.key }))}
                          className={`rounded-xl border-2 py-2.5 text-xs font-extrabold transition ${
                            form.yard_area === o.key
                              ? "border-[#047857] bg-emerald-50 text-[#047857]"
                              : "border-gray-200 text-gray-600 hover:border-gray-300"
                          }`}
                          data-testid={`property-yard-${o.key}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="prop-notes">Notes (optional)</Label>
              <Textarea
                id="prop-notes" rows={2}
                placeholder="Gate on left, dog in yard, etc."
                value={form.notes}
                onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
                data-testid="property-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={closeDialog}
                    data-testid="property-cancel">Cancel</Button>
            <Button
              onClick={save}
              disabled={busy === "save"}
              className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white"
              data-testid="property-save"
            >
              {busy === "save" ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
              ) : null}
              {editing === "new" ? "Add property" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="sm:max-w-md" data-testid="property-delete-dialog">
          <DialogHeader>
            <DialogTitle>Remove this property?</DialogTitle>
            <DialogDescription>
              Only properties with no active or completed bookings can be
              removed. Your service history stays untouched.
            </DialogDescription>
          </DialogHeader>
          {confirmDelete && (
            <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3 text-sm text-red-900">
              <b>{confirmDelete.label}</b> · {confirmDelete.address}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full"
                    onClick={() => setConfirmDelete(null)}
                    data-testid="property-delete-cancel">
              Keep property
            </Button>
            <Button
              onClick={doDelete}
              disabled={busy === "delete"}
              className="rounded-full bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="property-delete-confirm"
            >
              {busy === "delete" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
              Remove property
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function PropertyCard({ p, onEdit, onDelete, onSetDefault, busyId, onRefresh }) {
  const yardLabel = ({ front: "Front", back: "Back", both: "Full" }[p.yard_area] || "Full") + " yard";
  const rec = p.recurring;
  const isPaused = rec && rec.status === "paused";
  const isActive = rec && rec.status === "active";
  const recBadge = rec && (isActive || isPaused) ? (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
      isActive
        ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
        : "bg-amber-50 text-amber-800 border border-amber-200"
    }`} data-testid={`property-recurring-${p.property_id}`}>
      {isActive
        ? `Active · ${(rec.frequency_label || rec.frequency || "").replaceAll("_", " ")}`
        : `Paused${rec.resume_at ? ` · Resumes ${rec.resume_at}` : ""}`}
    </span>
  ) : null;
  // iter258 — while paused, HIDE the next-service pointer entirely and
  // show the resume date instead.  The crew must never see a phantom
  // upcoming visit for a paused property.
  const recNext = isPaused ? null : rec?.next_service_date;
  const recPrice = rec?.locked_price;
  const fmtDate = (ymd) => {
    if (!ymd) return "";
    const [y, m, d] = ymd.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 relative"
         data-testid={`property-card-${p.property_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Home className="w-4 h-4 text-[#047857]" />
            <span className="text-[15px] font-extrabold text-gray-900 truncate">
              {p.label || "Property"}
            </span>
            {p.is_default && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-[#047857] border border-emerald-200 text-[9px] font-extrabold uppercase tracking-wider"
                    data-testid={`property-default-badge-${p.property_id}`}>
                <Star className="w-2.5 h-2.5" /> Default
              </span>
            )}
            {recBadge}
          </div>
          <div className="mt-1 text-[13px] text-gray-600 truncate">{p.address}</div>
          <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px] font-bold">
            <span className="px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-700">
              {(p.square_feet || 0).toLocaleString()} sq ft
            </span>
            <span className="px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-700">
              {yardLabel}
            </span>
            {typeof recPrice === "number" && (
              <span className="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800">
                ${recPrice.toFixed(2)}/visit
              </span>
            )}
          </div>
          {recNext && (
            <div className="mt-2 text-[11px] font-bold text-gray-600" data-testid={`property-next-service-${p.property_id}`}>
              Next service: <span className="text-gray-900">{fmtDate(recNext)}</span>
            </div>
          )}
          {isPaused && rec?.resume_at && (
            <div className="mt-2 text-[11px] font-extrabold text-amber-800" data-testid={`property-paused-until-${p.property_id}`}>
              PAUSED — RESUMES <span className="text-amber-950">{fmtDate(rec.resume_at)}</span>
            </div>
          )}
          {p.notes && (
            <div className="mt-2 text-[12px] text-gray-500 line-clamp-2">{p.notes}</div>
          )}
        </div>
      </div>

      <RecurringExtrasList schedule={rec} propertyId={p.property_id} />
      <div className="mt-3">
        <PropertyAutopayToggle property={p} onChanged={onRefresh} />
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {!p.is_default && (
          <Button
            size="sm" variant="outline"
            className="rounded-full h-8 text-[11px]"
            onClick={onSetDefault}
            disabled={busyId === p.property_id}
            data-testid={`property-set-default-${p.property_id}`}
          >
            {busyId === p.property_id
              ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
              : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
            Set as default
          </Button>
        )}
        <Button size="sm" variant="outline"
                className="rounded-full h-8 text-[11px]"
                onClick={onEdit}
                data-testid={`property-edit-${p.property_id}`}>
          <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
        </Button>
        <Button size="sm" variant="outline"
                className="rounded-full h-8 text-[11px] border-red-200 text-red-700 hover:bg-red-50"
                onClick={onDelete}
                data-testid={`property-delete-${p.property_id}`}>
          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
        </Button>
      </div>
    </div>
  );
}
